/**
 * Section-specific helper copy (dashboard-flow-ux.md 6C / session 97).
 *
 * Replaces the old generic "Fill in the starred (*) fields to complete" /
 * "Add at least one X to complete" guidance with one purposeful sentence per
 * section — keyed by section slug. Shown ONLY when a section's status is
 * `attention` or `not_started` (see `section-status.ts`); `complete` and `na`
 * sections show nothing.
 *
 * A section not present in this map gets NO helper text — there is deliberately no
 * fallback to the generic starred-field string (it was removed). Add a new key here
 * when a section needs guidance.
 *
 * Keys use the `SECTION_SLUGS` constants (no hardcoded slug strings — `check:slugs`
 * pre-push hook). `beneficiaries` has no constant in `shared/constants/sections.ts`,
 * so it stays a literal until one exists.
 *
 * Copy intentionally uses the default "executor" wording (jurisdiction seam: the
 * future dictionary swaps the term where it matters — this is planner-facing
 * guidance, not an executor-facing label surface).
 */
import { SECTION_SLUGS } from "@shared/constants/sections";

export const SECTION_HELPER_TEXT: Record<string, string> = {
  // Legal
  // 🔴 s177 (A2, D-177.8): this section can NEVER be marked "doesn't apply" — every estate has an
  // executor, and a blank section here means UNDECIDED, not inapplicable. Since the honest exit is
  // removed, the copy must not read as a nag: it names the hesitation, normalises it, and offers
  // the one thing that actually unblocks the planner (knowing what the job involves).
  [SECTION_SLUGS.LEGAL_YOUR_EXECUTOR]:
    "Haven't decided yet? That's common — an executor gathers your assets, pays what's owed, and carries out your will. Name whoever you'd trust with that; you can change it any time.",
  [SECTION_SLUGS.LEGAL_IMPORTANT_DOCUMENTS]: "Record where your will and key legal documents are stored.",
  [SECTION_SLUGS.LEGAL_POWERS_OF_ATTORNEY]: "Add each power of attorney type, or remove rows that don't apply.",
  beneficiaries: "Add each beneficiary's name, relationship, and contact info so your executor can reach them.",
  [SECTION_SLUGS.LEGAL_PROFESSIONAL_CONTACTS]: "Record your lawyer, accountant, and other professional advisors.",
  [SECTION_SLUGS.LEGAL_BUSINESS_DOCUMENTS]: "Record business agreements your executor may need to settle the estate.",
  [SECTION_SLUGS.LEGAL_BUSINESS_CONTACTS]: "List contacts your executor should reach out to about your business interests.",

  // My Wishes & Legacy
  [SECTION_SLUGS.INSTRUCTIONS_FOR_EXECUTORS_FUNERAL_WISHES]: "Share your funeral and memorial preferences so your family knows your wishes.",
  [SECTION_SLUGS.INSTRUCTIONS_FOR_EXECUTORS_LETTERS]: "Write personal letters your executor can deliver to the people who matter most.",

  // Financial & Insurance
  [SECTION_SLUGS.FINANCIAL_BANK_ACCOUNTS]: "Record each bank where you hold accounts so your executor can notify them.",
  [SECTION_SLUGS.FINANCIAL_INVESTMENTS_RRSP]: "Add the institution for each investment you hold. Delete rows that don't apply.",
  [SECTION_SLUGS.FINANCIAL_AND_INSURANCE_INSURANCE_POLICIES]: "Record the type and provider for each insurance policy.",
  [SECTION_SLUGS.FINANCIAL_INCOME_PENSIONS]: "List your income sources so your executor knows what to expect or cancel.",
  [SECTION_SLUGS.FINANCIAL_DEBTS_LIABILITIES]: "Record each creditor and outstanding balance.",
  [SECTION_SLUGS.FINANCIAL_TAXES]: "Record recent tax years so your executor can file final returns.",
  [SECTION_SLUGS.FINANCIAL_BUSINESS_SELF_EMPLOYMENT]: "Record your business details so your executor can manage or wind down operations.",
  [SECTION_SLUGS.FINANCIAL_AND_INSURANCE_HOUSING]: "Add your rental details so your executor can notify landlords and manage leases.",
  [SECTION_SLUGS.FINANCIAL_AND_INSURANCE_PRE_AUTHORIZED_DEBITS]: "List recurring services so your executor knows what to cancel or transfer.",

  // Children & Dependents
  [SECTION_SLUGS.CHILDREN_DEPENDENTS_OVERVIEW]: "Add each child or dependant by name.",
  [SECTION_SLUGS.CHILDREN_DEPENDENTS_HEALTH]: "Record health and medical details for each child.",
  [SECTION_SLUGS.CHILDREN_DEPENDENTS_DAILY_CARE]: "Capture daily care routines so a guardian knows what each child needs.",
  [SECTION_SLUGS.CHILDREN_DEPENDENTS_EDUCATION]: "Record school and activity details for each child.",
  [SECTION_SLUGS.CHILDREN_DEPENDENTS_GUARDIANSHIP]: "Name the person you'd want to care for your children if needed.",
  [SECTION_SLUGS.CHILDREN_DEPENDENTS_OTHER]: "Note anything else a guardian should know about each child.",
  [SECTION_SLUGS.CHILDREN_DEPENDENTS_VALUES_WISHES]: "Share the values and wishes you want carried forward for each child.",
  [SECTION_SLUGS.CHILDREN_DEPENDENTS_FINANCIAL_SUPPORT]: "Record financial arrangements for each child's ongoing support.",
  [SECTION_SLUGS.CHILDREN_DEPENDENTS_GOV_IDS]: "Record identification documents for each child.",

  // Assets
  [SECTION_SLUGS.ASSETS_PROPERTIES]: "Add the address for each property you own.",
  [SECTION_SLUGS.ASSETS_VEHICLE_LIST]: "Record each vehicle. Delete rows that don't apply.",
  [SECTION_SLUGS.ASSETS_VALUABLES]: "Record valuable items your executor should know about.",

  // Personal Information
  [SECTION_SLUGS.PERSONAL_INFORMATION_HEALTH_MEDICAL]: "Record your health details and organ donor status.",
  [SECTION_SLUGS.FAMILY_FRIENDS_AND_PETS_IMPORTANT_CONTACTS]: "List people your executor should be able to reach.",
  [SECTION_SLUGS.PERSONAL_INFORMATION_GOVERNMENT_IDS]: "Record your government-issued identification.",
  [SECTION_SLUGS.PERSONAL_INFORMATION_HEALTH_INSURANCE]: "Record your health insurance coverage details.",
  [SECTION_SLUGS.FAMILY_PETS]: "Add your pets so your executor can arrange their care.",
  [SECTION_SLUGS.PERSONAL_INFORMATION_CAREER]: "Record your employers so your executor can notify HR and handle benefits.",

  // Online Presence
  [SECTION_SLUGS.PASSWORD_LOG_DEVICES_LOCAL_ACCESS]: "Record devices and how to access them.",
  [SECTION_SLUGS.PASSWORD_LOG_PRIMARY_EMAIL_IDENTITY_ACCOUNTS]: "Record your primary email and identity accounts.",
  [SECTION_SLUGS.PASSWORD_LOG_GOVERNMENT_TAX]: "Record government and tax portal logins.",
  [SECTION_SLUGS.PASSWORD_LOG_ONLINE_ACCOUNTS_COMMUNITIES]: "List online accounts your executor should know about.",
  // s171: PASSWORD_LOG_SHOPPING_LOYALTY_PROGRAMS entry removed — the section was retired in
  // FIELD-AUDIT-1 (D-152.11), folded into Online Accounts & Communities. It never renders, so
  // this string was unreachable copy for a dead surface.
  [SECTION_SLUGS.PASSWORD_LOG_DIGITAL_FILES_CLOUD_STORAGE]: "Record where your digital files and photos are stored.",
};

/** Helper copy for a section slug, or `undefined` when none is defined. */
export function sectionHelperText(slug: string): string | undefined {
  return SECTION_HELPER_TEXT[slug];
}
